import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';

/** Tasa en puntos porcentuales (ej. 21) cuando isPercent es true. */
export interface Tax {
  id: string;
  globalTaxId?: string;
  code: string;
  name: string;
  rate: number;
  isPercent: boolean;
  taxType: string;
  isInclusive: boolean;
  fiscalCode?: string;
  fiscalSystem?: string;
  isDisplayedOnInvoice: boolean;
  isActive: boolean;
  sortOrder: number;
}

export interface UpdateTenantTaxDto {
  rate: number;
  isActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TaxesService {
  private apiUrl = `${environment.apiBaseUrl}/api/v1/taxes`;

  constructor(
    private http: HttpClient,
    private toastr: ToastrService
  ) {}

  getTaxes(activeOnly?: boolean, options?: { silent?: boolean }): Observable<Tax[]> {
    let params = new HttpParams();
    if (activeOnly !== undefined && activeOnly !== null) {
      params = params.set('activeOnly', activeOnly.toString());
    }

    const req = this.http.get<Tax[]>(this.apiUrl, { params });
    if (options?.silent) {
      return req.pipe(catchError(() => of([])));
    }
    return req.pipe(catchError(error => this.handleError(error)));
  }

  getTaxById(id: string): Observable<Tax> {
    return this.http.get<Tax>(`${this.apiUrl}/${id}`).pipe(
      catchError(error => this.handleError(error))
    );
  }

  updateTax(id: string, dto: UpdateTenantTaxDto): Observable<boolean> {
    return this.http.put<boolean>(`${this.apiUrl}/${id}`, dto).pipe(
      map(response => {
        this.toastr.success('Impuesto actualizado correctamente', 'Éxito');
        return response;
      }),
      catchError(error => this.handleError(error))
    );
  }

  enableTax(id: string): Observable<unknown> {
    return this.http.patch(`${this.apiUrl}/${id}/enable`, {}).pipe(
      map(() => {
        this.toastr.success('Impuesto habilitado', 'Éxito');
        return true;
      }),
      catchError(error => this.handleError(error))
    );
  }

  disableTax(id: string): Observable<unknown> {
    return this.http.patch(`${this.apiUrl}/${id}/disable`, {}).pipe(
      map(() => {
        this.toastr.success('Impuesto deshabilitado', 'Éxito');
        return true;
      }),
      catchError(error => this.handleError(error))
    );
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'Ha ocurrido un error inesperado';

    if (error.error) {
      if (error.error.message) {
        errorMessage = error.error.message;
      } else if (typeof error.error === 'string') {
        errorMessage = error.error;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    this.toastr.error(errorMessage, 'Error');
    return throwError(() => error);
  }
}
